import React, { Component } from "react";
import { CButton, CTooltip } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

/**
 * ============================================================
 * SEKIN SECURITY
 * EmployeeTimeKeepingExport.js
 * ============================================================
 *
 * EXPORT BẢNG CHẤM CÔNG CỦA 01 NHÂN SỰ
 *
 * Một nhân sự có thể làm việc tại:
 *
 *   Mục tiêu A
 *      ├── Vị trí 1
 *      └── Vị trí 2
 *
 *   Mục tiêu B
 *      └── Vị trí 1
 *
 * Vì vậy dữ liệu được group theo:
 *
 *      project_id + position
 *
 * ============================================================
 *
 * PROPS DỰ KIẾN:
 *
 * employee
 * checks
 * projects
 * month
 *
 * Ví dụ:
 *
 * <EmployeeTimeKeepingExport
 *      employee={employee}
 *      checks={checks}
 *      projects={projects}
 *      month={month}
 * />
 *
 * ============================================================
 *
 * TEMPLATE:
 *
 * /templates/tpl_bang_cham_cong_nhan_su.xlsx
 *
 * ============================================================
 *
 * CẤU TRÚC TEMPLATE:
 *
 * A  = STT
 * B  = Mục tiêu
 * C  = Vị trí
 * D  = Thời gian làm việc
 * E  = ngày 1
 * ...
 * AI = ngày 31
 * AJ = Tổng giờ
 * AK = Tổng ngày
 *
 * ============================================================
 *
 * NGUYÊN TẮC QUAN TRỌNG:
 *
 * 1. KHÔNG mergeCells() trong exporter.
 * 2. KHÔNG spliceRows().
 * 3. KHÔNG insertRows().
 * 4. Giữ nguyên merged cells của template.
 * 5. Chỉ ghi dữ liệu vào các cell có sẵn.
 *
 * ============================================================
 */

export default class EmployeeTimeKeepingExport extends Component {

    constructor(props) {

        super(props);

        this.handleExport =
            this.handleExport.bind(this);
    }

    // ============================================================
    // DATE
    // ============================================================

    toDate(value) {

        if (!value) {
            return null;
        }

        if (value instanceof Date) {

            return isNaN(value.getTime())
                ? null
                : value;
        }

        // Firebase Timestamp
        if (
            value &&
            typeof value.toDate === "function"
        ) {

            const date =
                value.toDate();

            return isNaN(date.getTime())
                ? null
                : date;
        }

        // Firebase Timestamp dạng seconds
        if (
            value &&
            typeof value.seconds === "number"
        ) {

            const date =
                new Date(
                    value.seconds * 1000
                );

            return isNaN(date.getTime())
                ? null
                : date;
        }

        const date =
            new Date(value);

        return isNaN(date.getTime())
            ? null
            : date;
    }

    // ============================================================
    // MONTH
    // ============================================================

    getMonthDate() {

        const {
            month,
        } = this.props;

        const date =
            this.toDate(month);

        if (date) {
            return date;
        }

        console.warn(
            "[SEKIN EMPLOYEE EXPORT] month không hợp lệ, sử dụng tháng hiện tại."
        );

        return new Date();
    }

    getDaysInMonth() {

        const monthDate =
            this.getMonthDate();

        return new Date(
            monthDate.getFullYear(),
            monthDate.getMonth() + 1,
            0
        ).getDate();
    }

    // ============================================================
    // NUMBER
    // ============================================================

    round(value) {

        const number =
            Number(value);

        if (
            !Number.isFinite(number)
        ) {

            return 0;
        }

        return Math.round(
            (
                number +
                Number.EPSILON
            ) * 100
        ) / 100;
    }

    formatHours(value) {

        const number =
            this.round(value);

        if (
            number === 0
        ) {

            return "0";
        }

        if (
            Number.isInteger(number)
        ) {

            return String(number);
        }

        return number
            .toFixed(2)
            .replace(/0+$/, "")
            .replace(/\.$/, "");
    }

    // ============================================================
    // WEEKDAY
    // ============================================================

    getVietnameseWeekday(date) {

        const day =
            date.getDay();

        const weekdays = {

            0: "CN",
            1: "T2",
            2: "T3",
            3: "T4",
            4: "T5",
            5: "T6",
            6: "T7",
        };

        return weekdays[day] || "";
    }

    // ============================================================
    // EMPLOYEE
    // ============================================================

    getEmployee() {

        const {
            employee,
        } = this.props;

        if (
            employee &&
            employee.employee_id
        ) {

            return employee;
        }

        console.error(
            "[SEKIN EMPLOYEE EXPORT] Không có employee."
        );

        return null;
    }

    // ============================================================
    // PROJECT
    // ============================================================

    getProject(projectId) {

        const {
            projects = [],
        } = this.props;

        if (
            !Array.isArray(projects)
        ) {

            return null;
        }

        return (
            projects.find(
                project =>
                    String(project.id) ===
                    String(projectId)
            ) ||

            projects.find(
                project =>
                    String(project.project_id) ===
                    String(projectId)
            ) ||

            null
        );
    }

    // ============================================================
    // POSITION
    // ============================================================

    getPosition(check) {

        if (
            check &&
            check.position
        ) {

            return check.position;
        }

        return {};
    }

    // ============================================================
    // POSITION KEY
    // ============================================================

    getPositionKey(check) {

        const position =
            this.getPosition(check);

        if (
            position &&
            position.id
        ) {

            return String(
                position.id
            );
        }

        if (
            position &&
            position.position_id
        ) {

            return String(
                position.position_id
            );
        }

        if (
            position &&
            position.name
        ) {

            return String(
                position.name
            );
        }

        return "NO_POSITION";
    }

    // ============================================================
    // GROUP KEY
    // ============================================================

    getGroupKey(check) {

        const projectId =
            check &&
                check.project_id
                ? String(
                    check.project_id
                )
                : "NO_PROJECT";

        const positionKey =
            this.getPositionKey(
                check
            );

        return (
            projectId +
            "__" +
            positionKey
        );
    }

    // ============================================================
    // GET EMPLOYEE CHECKS
    // ============================================================

    getEmployeeChecks() {

        const employee =
            this.getEmployee();

        const {
            checks = [],
        } = this.props;

        if (!employee) {
            return [];
        }

        if (
            !Array.isArray(checks)
        ) {

            console.error(
                "[SEKIN EMPLOYEE EXPORT] checks không phải array."
            );

            return [];
        }

        const employeeId =
            String(
                employee.employee_id
            );

        const result =
            checks.filter(
                check => {

                    if (
                        !check ||
                        !check.employee_id
                    ) {

                        return false;
                    }

                    return (
                        String(
                            check.employee_id
                        ) === employeeId
                    );
                }
            );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Employee source: props.checks"
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] All checks:",
            checks.length
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Employee:",
            employee.fullname
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Employee ID:",
            employeeId
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Employee checks:",
            result.length
        );

        if (
            result.length > 0
        ) {

            console.log(
                "[SEKIN EMPLOYEE EXPORT] FIRST EMPLOYEE CHECK:",
                result[0]
            );
        }

        return result;
    }

    // ============================================================
    // FILTER MONTH
    // ============================================================

    getMonthChecks() {

        const checks =
            this.getEmployeeChecks();

        const monthDate =
            this.getMonthDate();

        const year =
            monthDate.getFullYear();

        const month =
            monthDate.getMonth();

        const result =
            checks.filter(
                check => {

                    const checkDate =
                        this.toDate(
                            check.check_date
                        );

                    if (!checkDate) {

                        return false;
                    }

                    return (
                        checkDate.getFullYear() ===
                        year &&
                        checkDate.getMonth() ===
                        month
                    );
                }
            );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] ========================================"
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] MONTH FILTER"
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Month:",
            month + 1,
            "/",
            year
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Checks after month filter:",
            result.length
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] ========================================"
        );

        return result;
    }

    // ============================================================
    // GROUP CHECKS BY PROJECT + POSITION
    // ============================================================

    groupChecks() {

        const checks =
            this.getMonthChecks();

        const groups = {};

        checks.forEach(
            check => {

                const key =
                    this.getGroupKey(
                        check
                    );

                if (
                    !groups[key]
                ) {

                    const project =
                        this.getProject(
                            check.project_id
                        );

                    const position =
                        this.getPosition(
                            check
                        );

                    groups[key] = {

                        key,

                        projectId:
                            check.project_id,

                        projectName:
                            project &&
                                project.name
                                ? project.name
                                : "",

                        positionId:
                            position &&
                                (
                                    position.id ||
                                    position.position_id
                                )
                                ? (
                                    position.id ||
                                    position.position_id
                                )
                                : "",

                        positionName:
                            position &&
                                position.name
                                ? position.name
                                : "",

                        positionDescription:
                            position &&
                                position.description
                                ? position.description
                                : "",

                        shift:
                            position &&
                                position.shift
                                ? position.shift
                                : null,

                        checks: [],
                    };
                }

                groups[key]
                    .checks
                    .push(check);
            }
        );

        const result =
            Object.keys(groups)
                .map(
                    key =>
                        groups[key]
                );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] ========================================"
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] GROUP RESULT"
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Group count:",
            result.length
        );

        result.forEach(
            (
                group,
                index
            ) => {

                console.log(
                    `[SEKIN EMPLOYEE EXPORT] GROUP ${index + 1}`,
                    {
                        projectId:
                            group.projectId,

                        project:
                            group.projectName,

                        position:
                            group.positionName,

                        checks:
                            group.checks.length,
                    }
                );
            }
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] ========================================"
        );

        return result;
    }

    // ============================================================
    // WORK TIME
    // ============================================================

    getWorkTime(group) {

        if (!group) {
            return "";
        }

        const shift =
            group.shift;

        if (!shift) {
            return "";
        }

        const start =
            shift.start ||
            "";

        const end =
            shift.end ||
            "";

        const duration =
            shift.duration
                ? Number(
                    shift.duration
                )
                : 0;

        // 18:00 - 06:00 (12/24)
        if (
            start &&
            end &&
            duration
        ) {

            return (
                `${start} - ${end} (${duration}/24)`
            );
        }

        if (
            start &&
            end
        ) {

            return (
                `${start} - ${end}`
            );
        }

        if (
            duration
        ) {

            return (
                `${duration}/24`
            );
        }

        return "";
    }

    // ============================================================
    // SHIFT DURATION
    // ============================================================

    getShiftDuration(group) {

        if (
            group &&
            group.shift &&
            group.shift.duration
        ) {

            const duration =
                Number(
                    group.shift.duration
                );

            if (
                Number.isFinite(
                    duration
                ) &&
                duration > 0
            ) {

                return duration;
            }
        }

        // Mặc định SEKIN:
        // ca 12 giờ
        return 12;
    }

    // ============================================================
    // GET DAY CHECKS
    // ============================================================

    getDayChecks(
        group,
        day
    ) {

        const monthDate =
            this.getMonthDate();

        const year =
            monthDate.getFullYear();

        const month =
            monthDate.getMonth();

        return group.checks.filter(
            check => {

                const checkDate =
                    this.toDate(
                        check.check_date
                    );

                if (!checkDate) {

                    return false;
                }

                return (
                    checkDate.getFullYear() ===
                    year &&
                    checkDate.getMonth() ===
                    month &&
                    checkDate.getDate() ===
                    day
                );
            }
        );
    }

    // ============================================================
    // GET DAY DATA
    // ============================================================

    getDayData(
        group,
        day
    ) {

        const dayChecks =
            this.getDayChecks(
                group,
                day
            );

        let regularHours =
            0;

        let overtimeHours =
            0;

        dayChecks.forEach(
            check => {

                const durationMinutes =
                    Number(
                        check.check_duration ||
                        0
                    );

                if (
                    !Number.isFinite(
                        durationMinutes
                    )
                ) {

                    return;
                }

                const durationHours =
                    durationMinutes /
                    60;

                if (
                    check.is_overtime
                ) {

                    overtimeHours +=
                        durationHours;

                } else {

                    regularHours +=
                        durationHours;
                }
            }
        );

        regularHours =
            this.round(
                regularHours
            );

        overtimeHours =
            this.round(
                overtimeHours
            );

        const totalHours =
            this.round(
                regularHours +
                overtimeHours
            );

        // ========================================================
        // HIỂN THỊ:
        //
        // 9
        // 12
        // 12(4)
        // ========================================================

        let display =
            "";

        if (
            regularHours > 0
        ) {

            display =
                this.formatHours(
                    regularHours
                );
        }

        if (
            overtimeHours > 0
        ) {

            display +=
                `(${this.formatHours(
                    overtimeHours
                )})`;
        }

        return {

            day,

            checks:
                dayChecks,

            regularHours,

            overtimeHours,

            totalHours,

            display,
        };
    }

    // ============================================================
    // BUILD GROUP DATA
    // ============================================================

    buildGroupData(
        group,
        index
    ) {

        const daysInMonth =
            this.getDaysInMonth();

        const days =
            [];

        let regularTotal =
            0;

        let overtimeTotal =
            0;

        for (
            let day = 1;
            day <= 31;
            day++
        ) {

            if (
                day <=
                daysInMonth
            ) {

                const dayData =
                    this.getDayData(
                        group,
                        day
                    );

                days.push(
                    dayData
                );

                regularTotal +=
                    dayData.regularHours;

                overtimeTotal +=
                    dayData.overtimeHours;

            } else {

                days.push({

                    day,

                    checks: [],

                    regularHours:
                        0,

                    overtimeHours:
                        0,

                    totalHours:
                        0,

                    display:
                        "",
                });
            }
        }

        regularTotal =
            this.round(
                regularTotal
            );

        overtimeTotal =
            this.round(
                overtimeTotal
            );

        const totalHours =
            this.round(
                regularTotal +
                overtimeTotal
            );

        // ========================================================
        // NGÀY CÔNG
        //
        // Chỉ giờ thường được tính thành ngày công.
        //
        // regularHours / shiftDuration
        // ========================================================

        const shiftDuration =
            this.getShiftDuration(
                group
            );

        const workingDays =
            this.round(
                regularTotal /
                shiftDuration
            );

        return {

            index:
                index + 1,

            projectId:
                group.projectId,

            projectName:
                group.projectName,

            positionId:
                group.positionId,

            positionName:
                group.positionName,

            positionDescription:
                group.positionDescription,

            workTime:
                this.getWorkTime(
                    group
                ),

            shiftDuration,

            days,

            regularTotal,

            overtimeTotal,

            totalHours,

            workingDays,
        };
    }

    // ============================================================
    // BUILD EXPORT DATA
    // ============================================================

    buildExportData() {

        const employee =
            this.getEmployee();

        if (!employee) {

            return null;
        }

        const groups =
            this.groupChecks();

        const rows =
            groups.map(
                (
                    group,
                    index
                ) =>
                    this.buildGroupData(
                        group,
                        index
                    )
            );

        let regularTotal =
            0;

        let overtimeTotal =
            0;

        let totalHours =
            0;

        let totalDays =
            0;

        rows.forEach(
            row => {

                regularTotal +=
                    row.regularTotal;

                overtimeTotal +=
                    row.overtimeTotal;

                totalHours +=
                    row.totalHours;

                totalDays +=
                    row.workingDays;
            }
        );

        const result = {

            employee,

            rows,

            regularTotal:
                this.round(
                    regularTotal
                ),

            overtimeTotal:
                this.round(
                    overtimeTotal
                ),

            totalHours:
                this.round(
                    totalHours
                ),

            totalDays:
                this.round(
                    totalDays
                ),
        };

        console.log(
            "[SEKIN EMPLOYEE EXPORT] ========================================"
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] FINAL EXPORT DATA"
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Employee:",
            employee.fullname
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Employee ID:",
            employee.employee_id
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Rows:",
            rows.length
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Regular:",
            result.regularTotal
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Overtime:",
            result.overtimeTotal
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Total:",
            result.totalHours
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Days:",
            result.totalDays
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] ========================================"
        );

        return result;
    }

    // ============================================================
    // FIND TOTAL ROW
    // ============================================================

    findTotalRow(
        worksheet
    ) {

        /*
         * Không hard-code row 19.
         *
         * Tìm cell có nội dung:
         *
         * "Tổng giờ/ngày:"
         *
         * để tương thích cả template 10 dòng
         * và template 50 dòng.
         */

        for (
            let rowNumber = 1;
            rowNumber <= worksheet.rowCount;
            rowNumber++
        ) {

            const cell =
                worksheet.getCell(
                    rowNumber,
                    1
                );

            const value =
                cell &&
                    cell.value !== null &&
                    cell.value !== undefined
                    ? String(
                        cell.value
                    ).trim()
                    : "";

            if (
                value.toLowerCase()
                    .includes(
                        "tổng giờ/ngày"
                    )
            ) {

                console.log(
                    "[SEKIN EMPLOYEE EXPORT] Total row found:",
                    rowNumber
                );

                return rowNumber;
            }
        }

        console.error(
            "[SEKIN EMPLOYEE EXPORT] Không tìm thấy dòng Tổng giờ/ngày."
        );

        return null;
    }

    // ============================================================
    // GET DETAIL ROW CAPACITY
    // ============================================================

    getDetailCapacity(
        worksheet,
        firstDataRow,
        totalRow
    ) {

        if (
            !totalRow
        ) {

            return 0;
        }

        return (
            totalRow -
            firstDataRow
        );
    }

    // ============================================================
    // CLEAR CELL
    // ============================================================

    clearCellValue(
        worksheet,
        rowNumber,
        columnNumber
    ) {

        const cell =
            worksheet.getCell(
                rowNumber,
                columnNumber
            );

        /*
         * Chỉ xoá VALUE.
         *
         * Không đụng:
         * font
         * fill
         * border
         * alignment
         * merge
         */

        cell.value =
            null;
    }

    // ============================================================
    // CLEAR DETAIL ROW
    // ============================================================

    clearDetailRow(
        worksheet,
        rowNumber
    ) {

        for (
            let column = 1;
            column <= 37;
            column++
        ) {

            this.clearCellValue(
                worksheet,
                rowNumber,
                column
            );
        }
    }

    // ============================================================
    // WRITE DETAIL ROW
    // ============================================================

    writeDetailRow(
        worksheet,
        rowNumber,
        data
    ) {

        const row =
            worksheet.getRow(
                rowNumber
            );

        // ========================================================
        // STT
        // ========================================================

        row.getCell(
            1
        ).value =
            data.index;

        // ========================================================
        // MỤC TIÊU
        // ========================================================

        row.getCell(
            2
        ).value =
            data.projectName ||
            "";

        // ========================================================
        // VỊ TRÍ
        // ========================================================

        let positionText =
            data.positionName ||
            "";

        /*
         * Nếu có description thì đưa xuống dòng.
         */

        if (
            data.positionDescription
        ) {

            if (
                positionText
            ) {

                positionText +=
                    `\n${data.positionDescription}`;

            } else {

                positionText =
                    data.positionDescription;
            }
        }

        row.getCell(
            3
        ).value =
            positionText;

        // ========================================================
        // THỜI GIAN
        // ========================================================

        row.getCell(
            4
        ).value =
            data.workTime ||
            "";

        // ========================================================
        // 31 NGÀY
        // ========================================================

        for (
            let day = 1;
            day <= 31;
            day++
        ) {

            const column =
                day + 4;

            const cell =
                row.getCell(
                    column
                );

            const dayData =
                data.days[
                day - 1
                ];

            if (
                dayData &&
                dayData.display
            ) {

                cell.value =
                    dayData.display;

            } else {

                cell.value =
                    "";
            }

            cell.alignment = {

                horizontal:
                    "center",

                vertical:
                    "middle",

                wrapText:
                    true,
            };
        }

        // ========================================================
        // TỔNG GIỜ
        // ========================================================

        row.getCell(
            36
        ).value =
            data.totalHours;

        row.getCell(
            36
        ).numFmt =
            "0.00";

        // ========================================================
        // NGÀY CÔNG
        // ========================================================

        row.getCell(
            37
        ).value =
            data.workingDays;

        row.getCell(
            37
        ).numFmt =
            "0.00";

        // ========================================================
        // LOG
        // ========================================================

        console.log(
            `[SEKIN EMPLOYEE EXPORT] Writing row ${rowNumber}`,
            {
                index:
                    data.index,

                project:
                    data.projectName,

                position:
                    data.positionName,

                totalHours:
                    data.totalHours,

                totalDays:
                    data.workingDays,
            }
        );
    }

    // ============================================================
    // WRITE TOTAL ROW
    // ============================================================

    writeTotalRow(
        worksheet,
        totalRow,
        data
    ) {

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Writing total row:",
            totalRow
        );

        // ========================================================
        // LABEL
        // ========================================================

        worksheet.getCell(
            totalRow,
            1
        ).value =
            "Tổng giờ/ngày:";

        // ========================================================
        // TOTAL HOURS EACH DAY
        // ========================================================

        for (
            let day = 1;
            day <= 31;
            day++
        ) {

            let total =
                0;

            data.rows.forEach(
                row => {

                    const dayData =
                        row.days[
                        day - 1
                        ];

                    if (
                        dayData
                    ) {

                        total +=
                            Number(
                                dayData.totalHours ||
                                0
                            );
                    }
                }
            );

            total =
                this.round(
                    total
                );

            const cell =
                worksheet.getCell(
                    totalRow,
                    day + 4
                );

            cell.value =
                total;

            cell.numFmt =
                "0.00";
        }

        // ========================================================
        // GRAND TOTAL HOURS
        // ========================================================

        worksheet.getCell(
            totalRow,
            36
        ).value =
            data.totalHours;

        worksheet.getCell(
            totalRow,
            36
        ).numFmt =
            "0.00";

        // ========================================================
        // GRAND TOTAL DAYS
        // ========================================================

        worksheet.getCell(
            totalRow,
            37
        ).value =
            data.totalDays;

        worksheet.getCell(
            totalRow,
            37
        ).numFmt =
            "0.00";

        console.log(
            "[SEKIN EMPLOYEE EXPORT] TOTAL:",
            {
                totalHours:
                    data.totalHours,

                totalDays:
                    data.totalDays,
            }
        );
    }

    // ============================================================
    // WRITE SUMMARY
    // ============================================================

    writeSummary(
        worksheet,
        totalRow,
        data
    ) {

        /*
         * Không merge.
         *
         * Chỉ ghi vào các cell đã merge sẵn
         * trong template.
         */

        const titleRow =
            totalRow + 2;

        const headerRow =
            totalRow + 3;

        const regularRow =
            totalRow + 4;

        const overtimeRow =
            totalRow + 5;

        const grandRow =
            totalRow + 6;

        // ========================================================
        // TITLE
        // ========================================================

        if (
            worksheet.getRow(
                titleRow
            )
        ) {

            worksheet.getCell(
                titleRow,
                1
            ).value =
                "TỔNG HỢP CHẤM CÔNG";
        }

        // ========================================================
        // HEADER
        // ========================================================

        worksheet.getCell(
            headerRow,
            1
        ).value =
            "Nội dung";

        worksheet.getCell(
            headerRow,
            7
        ).value =
            "Tổng giờ";

        worksheet.getCell(
            headerRow,
            10
        ).value =
            "Ngày công";

        // ========================================================
        // REGULAR
        // ========================================================

        const regularDays =
            this.round(
                data.rows.reduce(
                    (
                        total,
                        row
                    ) =>
                        total +
                        Number(
                            row.workingDays ||
                            0
                        ),
                    0
                )
            );

        worksheet.getCell(
            regularRow,
            1
        ).value =
            "Giờ công thường";

        worksheet.getCell(
            regularRow,
            7
        ).value =
            data.regularTotal;

        worksheet.getCell(
            regularRow,
            10
        ).value =
            regularDays;

        // ========================================================
        // OVERTIME
        // ========================================================

        worksheet.getCell(
            overtimeRow,
            1
        ).value =
            "Giờ tăng ca";

        worksheet.getCell(
            overtimeRow,
            7
        ).value =
            data.overtimeTotal;

        worksheet.getCell(
            overtimeRow,
            10
        ).value =
            "";

        // ========================================================
        // GRAND TOTAL
        // ========================================================

        worksheet.getCell(
            grandRow,
            1
        ).value =
            "Tổng cộng";

        worksheet.getCell(
            grandRow,
            7
        ).value =
            data.totalHours;

        worksheet.getCell(
            grandRow,
            10
        ).value =
            data.totalDays;

        // ========================================================
        // NUMBER FORMAT
        // ========================================================

        [
            regularRow,
            overtimeRow,
            grandRow,
        ].forEach(
            rowNumber => {

                worksheet.getCell(
                    rowNumber,
                    7
                ).numFmt =
                    "0.00";
            }
        );

        [
            regularRow,
            grandRow,
        ].forEach(
            rowNumber => {

                worksheet.getCell(
                    rowNumber,
                    10
                ).numFmt =
                    "0.00";
            }
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Summary written:",
            {
                titleRow,
                headerRow,
                regularRow,
                overtimeRow,
                grandRow,
            }
        );
    }

    // ============================================================
    // WRITE HEADER
    // ============================================================

    writeHeader(
        worksheet,
        data
    ) {

        const employee =
            data.employee;

        const monthDate =
            this.getMonthDate();

        // ========================================================
        // EMPLOYEE
        // ========================================================

        worksheet.getCell(
            "A4"
        ).value =
            `Nhân sự: ${employee.fullname ||
            ""
            }`;

        // ========================================================
        // EMPLOYEE ID
        // ========================================================

        worksheet.getCell(
            "I4"
        ).value =
            `MSNV: ${employee.employee_id ||
            ""
            }`;

        // ========================================================
        // MONTH
        // ========================================================

        worksheet.getCell(
            "N4"
        ).value =
            `Tháng: ${String(
                monthDate.getMonth() + 1
            ).padStart(
                2,
                "0"
            )
            }/${monthDate.getFullYear()
            }`;

        // ========================================================
        // EXPORT DATE
        // ========================================================

        const now =
            new Date();

        worksheet.getCell(
            "S4"
        ).value =
            `Ngày xuất: ngày ${now.getDate()
            } tháng ${now.getMonth() + 1
            } năm ${now.getFullYear()
            }`;

        // ========================================================
        // NOTE
        // ========================================================

        worksheet.getCell(
            "A5"
        ).value =
            "Quy ước: Giờ công thường ghi trực tiếp; giờ tăng ca ghi trong ngoặc. Ví dụ: 12 (4) = 12 giờ thường + 4 giờ tăng ca.";

        // ========================================================
        // DAY HEADER
        // ========================================================

        const daysInMonth =
            this.getDaysInMonth();

        for (
            let day = 1;
            day <= 31;
            day++
        ) {

            const dayColumn =
                day + 4;

            const dayCell =
                worksheet.getCell(
                    7,
                    dayColumn
                );

            const weekdayCell =
                worksheet.getCell(
                    8,
                    dayColumn
                );

            if (
                day <=
                daysInMonth
            ) {

                dayCell.value =
                    day;

                const date =
                    new Date(
                        monthDate.getFullYear(),
                        monthDate.getMonth(),
                        day
                    );

                weekdayCell.value =
                    this.getVietnameseWeekday(
                        date
                    );

            } else {

                dayCell.value =
                    "";

                weekdayCell.value =
                    "";
            }
        }
    }

    // ============================================================
    // LOAD TEMPLATE
    // ============================================================

    async loadTemplate() {

        const templateUrl =
            "/templates/tpl_bang_cham_cong_nhan_su.xlsx";

        console.log(
            "[SEKIN EMPLOYEE EXPORT] ========================================"
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Loading template:",
            templateUrl
        );

        const response =
            await fetch(
                templateUrl,
                {
                    method:
                        "GET",

                    cache:
                        "no-store",
                }
            );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] HTTP status:",
            response.status
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Content-Type:",
            response.headers.get(
                "content-type"
            )
        );

        if (
            !response.ok
        ) {

            throw new Error(
                `Không tải được template. HTTP ${response.status}`
            );
        }

        const contentType =
            response.headers.get(
                "content-type"
            ) || "";

        if (
            contentType.includes(
                "text/html"
            )
        ) {

            throw new Error(
                "URL template đang trả về HTML. Kiểm tra file trong /public/templates/."
            );
        }

        const buffer =
            await response.arrayBuffer();

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Template size:",
            buffer.byteLength,
            "bytes"
        );

        const workbook =
            new ExcelJS.Workbook();

        await workbook.xlsx.load(
            buffer
        );

        const worksheet =
            workbook.getWorksheet(
                "Bảng chấm công"
            ) ||
            workbook.worksheets[0];

        if (
            !worksheet
        ) {

            throw new Error(
                "Không tìm thấy worksheet trong template."
            );
        }

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Workbook loaded."
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Worksheet:",
            worksheet.name
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Row count:",
            worksheet.rowCount
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Column count:",
            worksheet.columnCount
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] ========================================"
        );

        return {

            workbook,

            worksheet,
        };
    }

    // ============================================================
    // VERIFY
    // ============================================================

    verifyWorkbook(
        worksheet,
        data,
        totalRow
    ) {

        console.log(
            "[SEKIN EMPLOYEE EXPORT] ========================================"
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] VERIFY EXCEL"
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Employee:",
            worksheet.getCell(
                "A4"
            ).value
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Employee ID:",
            worksheet.getCell(
                "I4"
            ).value
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Total row:",
            totalRow
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Employee group count:",
            data.rows.length
        );

        if (
            data.rows.length > 0
        ) {

            console.log(
                "[SEKIN EMPLOYEE EXPORT] ROW 9:",
                {
                    A9:
                        worksheet.getCell(
                            "A9"
                        ).value,

                    B9:
                        worksheet.getCell(
                            "B9"
                        ).value,

                    C9:
                        worksheet.getCell(
                            "C9"
                        ).value,

                    D9:
                        worksheet.getCell(
                            "D9"
                        ).value,

                    E9:
                        worksheet.getCell(
                            "E9"
                        ).value,

                    AJ9:
                        worksheet.getCell(
                            "AJ9"
                        ).value,

                    AK9:
                        worksheet.getCell(
                            "AK9"
                        ).value,
                }
            );

            const lastDataRow =
                8 +
                data.rows.length;

            console.log(
                "[SEKIN EMPLOYEE EXPORT] LAST DATA ROW:",
                lastDataRow
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] LAST DATA:",
                {
                    A:
                        worksheet.getCell(
                            lastDataRow,
                            1
                        ).value,

                    B:
                        worksheet.getCell(
                            lastDataRow,
                            2
                        ).value,

                    C:
                        worksheet.getCell(
                            lastDataRow,
                            3
                        ).value,

                    E:
                        worksheet.getCell(
                            lastDataRow,
                            5
                        ).value,

                    AJ:
                        worksheet.getCell(
                            lastDataRow,
                            36
                        ).value,

                    AK:
                        worksheet.getCell(
                            lastDataRow,
                            37
                        ).value,
                }
            );
        }

        console.log(
            "[SEKIN EMPLOYEE EXPORT] TOTAL ROW DATA:",
            {
                A:
                    worksheet.getCell(
                        totalRow,
                        1
                    ).value,

                E:
                    worksheet.getCell(
                        totalRow,
                        5
                    ).value,

                AJ:
                    worksheet.getCell(
                        totalRow,
                        36
                    ).value,

                AK:
                    worksheet.getCell(
                        totalRow,
                        37
                    ).value,
            }
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] ========================================"
        );
    }

    // ============================================================
    // FILENAME
    // ============================================================

    sanitizeFileName(
        value
    ) {

        return String(
            value || ""
        )
            .replace(
                /[\\/:*?"<>|]/g,
                "-"
            )
            .replace(
                /\s+/g,
                " "
            )
            .trim();
    }

    // ============================================================
    // BUILD FILE NAME
    // ============================================================

    buildFileName(
        employee
    ) {

        const monthDate =
            this.getMonthDate();

        const monthText =
            String(
                monthDate.getMonth() + 1
            ).padStart(
                2,
                "0"
            ) +
            "-" +
            monthDate.getFullYear();

        const employeeName =
            this.sanitizeFileName(
                employee.fullname ||
                "NhanSu"
            );

        const employeeId =
            this.sanitizeFileName(
                employee.employee_id ||
                ""
            );

        return (
            `SEKIN_BẢNG_CHẤM_CÔNG_${employeeName}_${employeeId}_${monthText}.xlsx`
        );
    }

    // ============================================================
    // PREPARE WORKBOOK
    // ============================================================

    async prepareWorkbook(
        data
    ) {

        const {
            workbook,
            worksheet,
        } =
            await this.loadTemplate();

        // ========================================================
        // HEADER
        // ========================================================

        this.writeHeader(
            worksheet,
            data
        );

        // ========================================================
        // FIND TOTAL ROW
        // ========================================================

        const firstDataRow =
            9;

        const totalRow =
            this.findTotalRow(
                worksheet
            );

        if (
            !totalRow
        ) {

            throw new Error(
                "Không tìm thấy dòng 'Tổng giờ/ngày:' trong template."
            );
        }

        // ========================================================
        // CAPACITY
        // ========================================================

        const capacity =
            this.getDetailCapacity(
                worksheet,
                firstDataRow,
                totalRow
            );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] ========================================"
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] TEMPLATE CAPACITY"
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] First data row:",
            firstDataRow
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Total row:",
            totalRow
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Detail capacity:",
            capacity
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] Data rows:",
            data.rows.length
        );

        console.log(
            "[SEKIN EMPLOYEE EXPORT] ========================================"
        );

        // ========================================================
        // CHECK CAPACITY
        // ========================================================

        if (
            data.rows.length >
            capacity
        ) {

            throw new Error(
                `Template chỉ có ${capacity} dòng chi tiết nhưng nhân sự này cần ${data.rows.length} dòng. Hãy mở rộng template tpl_bang_cham_cong_nhan_su.xlsx để có thêm dòng dữ liệu.`
            );
        }

        // ========================================================
        // CLEAR ALL DETAIL ROWS
        // ========================================================

        for (
            let rowNumber =
                firstDataRow;
            rowNumber <
            totalRow;
            rowNumber++
        ) {

            this.clearDetailRow(
                worksheet,
                rowNumber
            );
        }

        // ========================================================
        // WRITE DATA
        // ========================================================

        data.rows.forEach(
            (
                rowData,
                index
            ) => {

                const rowNumber =
                    firstDataRow +
                    index;

                this.writeDetailRow(
                    worksheet,
                    rowNumber,
                    rowData
                );
            }
        );

        // ========================================================
        // WRITE TOTAL
        // ========================================================

        this.writeTotalRow(
            worksheet,
            totalRow,
            data
        );

        // ========================================================
        // WRITE SUMMARY
        // ========================================================

        this.writeSummary(
            worksheet,
            totalRow,
            data
        );

        // ========================================================
        // CALCULATION
        // ========================================================

        if (
            workbook.calcProperties
        ) {

            workbook
                .calcProperties
                .calcMode =
                "auto";

            workbook
                .calcProperties
                .fullCalcOnLoad =
                true;

            workbook
                .calcProperties
                .forceFullCalc =
                true;
        }

        // ============================================================
        // VIEW
        // ============================================================

        worksheet.views = [
            {
                state: "frozen",

                xSplit: 4,

                ySplit: 8,

                showGridLines: false,
            },
        ];

        // ========================================================
        // PRINT
        // ========================================================

        worksheet.pageSetup = {

            orientation:
                "landscape",

            fitToPage:
                true,

            fitToWidth:
                1,

            fitToHeight:
                0,

            horizontalCentered:
                true,

            verticalCentered:
                false,

            margins: {

                left:
                    0.2,

                right:
                    0.2,

                top:
                    0.4,

                bottom:
                    0.4,

                header:
                    0.2,

                footer:
                    0.2,
            },
        };


        return {

            workbook,

            worksheet,

            totalRow,
        };
    }

    // ============================================================
    // HANDLE EXPORT
    // ============================================================

    async handleExport() {

        try {

            console.log(
                "[SEKIN EMPLOYEE EXPORT] ========================================"
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] START EXPORT"
            );

            // ====================================================
            // EMPLOYEE
            // ====================================================

            const employee =
                this.getEmployee();

            if (!employee) {

                alert(
                    "Không xác định được nhân sự cần xuất bảng công."
                );

                return;
            }

            // ====================================================
            // DATA
            // ====================================================

            const data =
                this.buildExportData();

            if (!data) {

                alert(
                    "Không thể tạo dữ liệu bảng công."
                );

                return;
            }

            // ====================================================
            // NO DATA
            // ====================================================

            if (
                data.rows.length === 0
            ) {

                const shouldExport =
                    window.confirm(
                        `Nhân sự ${employee.fullname || ""} không có dữ liệu chấm công trong tháng này.\n\nBạn có muốn xuất bảng công trống không?`
                    );

                if (
                    !shouldExport
                ) {

                    return;
                }
            }

            // ====================================================
            // PREPARE WORKBOOK
            // ====================================================

            const {
                workbook,
                worksheet,
                totalRow,
            } =
                await this.prepareWorkbook(
                    data
                );

            // ====================================================
            // VERIFY BEFORE WRITE
            // ====================================================

            this.verifyWorkbook(
                worksheet,
                data,
                totalRow
            );

            // ====================================================
            // GENERATE
            // ====================================================

            console.log(
                "[SEKIN EMPLOYEE EXPORT] ========================================"
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] Generating XLSX..."
            );

            const outputBuffer =
                await workbook.xlsx.writeBuffer();

            console.log(
                "[SEKIN EMPLOYEE EXPORT] XLSX generated:",
                outputBuffer.byteLength,
                "bytes"
            );

            // ====================================================
            // FILE NAME
            // ====================================================

            const fileName =
                this.buildFileName(
                    employee
                );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] File name:",
                fileName
            );

            // ====================================================
            // BLOB
            // ====================================================

            const blob =
                new Blob(
                    [
                        outputBuffer,
                    ],
                    {
                        type:
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    }
                );

            // ====================================================
            // SAVE
            // ====================================================

            saveAs(
                blob,
                fileName
            );

            // ====================================================
            // COMPLETE
            // ====================================================

            console.log(
                "[SEKIN EMPLOYEE EXPORT] ========================================"
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] EXPORT COMPLETED"
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] Employee:",
                employee.fullname
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] Employee ID:",
                employee.employee_id
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] Groups:",
                data.rows.length
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] Regular:",
                data.regularTotal
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] Overtime:",
                data.overtimeTotal
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] Total:",
                data.totalHours
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] Days:",
                data.totalDays
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] File:",
                fileName
            );

            console.log(
                "[SEKIN EMPLOYEE EXPORT] ========================================"
            );

        } catch (
        error
        ) {

            console.error(
                "[SEKIN EMPLOYEE EXPORT] ========================================"
            );

            console.error(
                "[SEKIN EMPLOYEE EXPORT] EXPORT ERROR:",
                error
            );

            console.error(
                "[SEKIN EMPLOYEE EXPORT] MESSAGE:",
                error &&
                error.message
            );

            console.error(
                "[SEKIN EMPLOYEE EXPORT] STACK:",
                error &&
                error.stack
            );

            console.error(
                "[SEKIN EMPLOYEE EXPORT] ========================================"
            );

            alert(
                "Không thể xuất bảng chấm công nhân sự.\n\n" +
                (
                    error &&
                        error.message
                        ? error.message
                        : "Lỗi không xác định."
                )
            );
        }
    }

    // ============================================================
    // RENDER
    // ============================================================

    render() {

        return (

            <CTooltip
                content={
                    "Xuất bảng chấm công nhân sự"
                }
            >

                <CButton
                    color="info"
                    size="sm"
                    className="ml-1"
                    title={
                        "Xuất bảng chấm công nhân sự"
                    }
                    onClick={
                        this.handleExport
                    }
                >

                    <CIcon
                        name="cil-save"
                        className="mfe-1 mt-0"
                    />

                    Export

                </CButton>

            </CTooltip>
        );
    }
}